import React from 'react';
import { ArrowLeft, Ban, DollarSign, Package, Receipt } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import useCantinaCierre from '../hooks/cantina/useCantinaCierre';

const dinero = (valor) =>
  new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(valor || 0));

function TablaResumen({ titulo, columnas, filas, vacio }) {
  return (
    <section className="overflow-hidden rounded-xl border border-white/10 bg-slate-800/80">
      <div className="border-b border-blanco-puro/10 px-5 py-4">
        <h2 className="font-semibold">{titulo}</h2>
      </div>

      {filas.length === 0 ? (
        <div className="p-5 text-sm text-blanco-puro/50">
          {vacio}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-950/70 text-left text-xs uppercase tracking-wide text-blanco-puro/50">
              <tr>
                {columnas.map((columna) => (
                  <th key={columna.key} className="px-5 py-3">
                    {columna.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filas.map((fila, index) => (
                <tr
                  key={fila.id || index}
                  className="border-t border-blanco-puro/10"
                >
                  {columnas.map((columna) => (
                    <td key={columna.key} className="px-5 py-3">
                      {columna.render
                        ? columna.render(fila)
                        : fila[columna.key]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export default function CantinaCierre() {
  const navigate = useNavigate();
  const {
    filtros,
    cierre,
    cargando,
    error,
    actualizarFiltro,
  } = useCantinaCierre();

  const totales = cierre?.totales || {};

  const tarjetas = [
    {
      titulo: 'Recaudación',
      valor: dinero(totales.monto),
      detalle: `${totales.ventas || 0} ventas completadas`,
      icono: DollarSign,
    },
    {
      titulo: 'Unidades vendidas',
      valor: totales.unidades || 0,
      detalle: 'Productos de ventas válidas',
      icono: Package,
    },
    {
      titulo: 'Ventas',
      valor: totales.ventas || 0,
      detalle: 'Operaciones completadas',
      icono: Receipt,
    },
    {
      titulo: 'Anulaciones',
      valor: dinero(totales.monto_anulado),
      detalle: `${totales.ventas_anuladas || 0} ventas anuladas`,
      icono: Ban,
    },
  ];

  return (
    <>
      <Navbar />

      <main className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-900 to-black px-4 pb-12 pt-24 text-blanco-puro">
        <div className="mx-auto max-w-7xl">
          <button
            type="button"
            onClick={() => navigate('/ventas')}
            className="mb-5 flex items-center gap-2 text-sm text-blanco-puro/70 transition hover:text-blanco-puro"
          >
            <ArrowLeft size={17} />
            Volver a Cantina
          </button>

          <div className="mb-7 flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-amarillo-dorado">
                Cantina
              </p>
              <h1 className="mt-1 text-3xl font-bold">
                Cierre de Cantina
              </h1>
              <p className="mt-2 text-sm text-blanco-puro/65">
                Resumen de ventas, cobros, productos y anulaciones.
              </p>
            </div>

            <div className="flex flex-wrap gap-3 rounded-xl border border-white/10 bg-slate-800/80 p-4">
              <label className="text-xs text-blanco-puro/70">
                Desde
                <input
                  type="date"
                  value={filtros.desde}
                  onChange={(e) =>
                    actualizarFiltro('desde', e.target.value)
                  }
                  className="mt-1 block rounded-lg border border-white/20 bg-slate-950 px-3 py-2 text-sm text-blanco-puro"
                />
              </label>

              <label className="text-xs text-blanco-puro/70">
                Hasta
                <input
                  type="date"
                  value={filtros.hasta}
                  onChange={(e) =>
                    actualizarFiltro('hasta', e.target.value)
                  }
                  className="mt-1 block rounded-lg border border-white/20 bg-slate-950 px-3 py-2 text-sm text-blanco-puro"
                />
              </label>
            </div>
          </div>

          {error && (
            <div className="mb-5 rounded-xl border border-red-400/30 bg-red-500/10 p-4 text-sm text-red-200">
              {error}
            </div>
          )}

          {cargando && !cierre ? (
            <div className="rounded-xl border border-white/10 bg-slate-800/80 p-8 text-center text-blanco-puro/60">
              Cargando cierre...
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {tarjetas.map((tarjeta) => {
                const Icono = tarjeta.icono;

                return (
                  <div
                    key={tarjeta.titulo}
                    className="rounded-xl border border-white/10 bg-slate-800/80 p-5"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-blanco-puro/65">
                        {tarjeta.titulo}
                      </span>
                      <Icono
                        size={19}
                        className="text-amarillo-dorado"
                      />
                    </div>

                    <div className="mt-3 text-2xl font-bold">
                      {tarjeta.valor}
                    </div>

                    <div className="mt-1 text-xs text-blanco-puro/50">
                      {tarjeta.detalle}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {cierre && (
            <div className="mt-6 grid gap-6">
              <TablaResumen
                titulo="Medios de pago"
                filas={(cierre.por_medio_pago || []).map((fila) => ({
                  ...fila,
                  id: fila.medio_pago_id,
                }))}
                vacio="No hay cobros en el período seleccionado."
                columnas={[
                  { key: 'nombre', label: 'Medio de pago' },
                  { key: 'ventas', label: 'Ventas' },
                  {
                    key: 'monto',
                    label: 'Recaudación',
                    render: (fila) => dinero(fila.monto),
                  },
                ]}
              />

              <TablaResumen
                titulo="Ventas por usuario"
                filas={(cierre.por_usuario || []).map((fila) => ({
                  ...fila,
                  id: fila.usuario_id,
                }))}
                vacio="No hay ventas por usuario en el período."
                columnas={[
                  { key: 'nombre', label: 'Usuario' },
                  { key: 'ventas', label: 'Ventas' },
                  {
                    key: 'monto',
                    label: 'Recaudación',
                    render: (fila) => dinero(fila.monto),
                  },
                ]}
              />

              <TablaResumen
                titulo="Productos vendidos"
                filas={(cierre.productos || []).map((fila) => ({
                  ...fila,
                  id: fila.producto_id,
                }))}
                vacio="No hay productos vendidos en el período."
                columnas={[
                  { key: 'nombre', label: 'Producto' },
                  { key: 'cantidad', label: 'Cantidad' },
                  {
                    key: 'monto',
                    label: 'Total',
                    render: (fila) => dinero(fila.monto),
                  },
                ]}
              />
            </div>
          )}
        </div>
      </main>
    </>
  );
}
